// 🖐 고정(fixed) 요소를 손가락·마우스로 끌어 옮기기 (2026-09-10 사용자 "안드 앱 보이스톡 위치를 눌러서 이동").
//   요소는 종전 자리(cornerClass 등)에 그대로 두고, 끈 만큼을 CSS `translate` 로 얹는다 — Tailwind `-translate-x-1/2`(transform)와
//   독립된 속성이라 가운데 정렬과도 겹치지 않는다. 버튼·링크·입력 위에서 시작한 제스처는 무시(탭이 눌려야 하므로),
//   4px 미만 움직임은 드래그로 보지 않는다. 화면 밖으로 나가지 않게 뷰포트 안으로 클램프. reset() 으로 원위치.
//   사용: const drag = useDragOffset();
//        <div data-drag-root style={drag.style}> <div {...drag.handleProps}>손잡이(헤더)</div> … </div>
//   클램프 기준 사각형은 손잡이의 가장 가까운 [data-drag-root](없으면 손잡이 자신).
import { useCallback, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';

const IGNORE = 'button, a, input, select, textarea, [role="button"]';
const THRESHOLD = 4;

export function useDragOffset() {
	const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
	const start = useRef<{ px: number; py: number; ox: number; oy: number; el: HTMLElement; moved: boolean } | null>(null);

	const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
		if (e.button !== 0) return;
		if ((e.target as HTMLElement).closest(IGNORE)) return;
		const handle = e.currentTarget;
		const el = (handle.closest('[data-drag-root]') as HTMLElement | null) || handle;
		start.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y, el, moved: false };
		try { handle.setPointerCapture(e.pointerId); } catch { /* 미지원 */ }
	}, [offset.x, offset.y]);

	const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
		const s = start.current;
		if (!s) return;
		const dx = e.clientX - s.px, dy = e.clientY - s.py;
		if (!s.moved && Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
		s.moved = true;
		e.preventDefault();
		// 뷰포트 클램프 — 현재 사각형(translate 반영)에서 이번 이동을 뺀 기준 위치로 계산
		const r = s.el.getBoundingClientRect();
		const baseL = r.left - offset.x, baseT = r.top - offset.y;
		const nx = Math.min(Math.max(s.ox + dx, -baseL), window.innerWidth - r.width - baseL);
		const ny = Math.min(Math.max(s.oy + dy, -baseT), window.innerHeight - r.height - baseT);
		setOffset({ x: Math.round(nx), y: Math.round(ny) });
	}, [offset.x, offset.y]);

	const onPointerUp = useCallback((e: ReactPointerEvent<HTMLElement>) => {
		const s = start.current;
		start.current = null;
		if (!s) return;
		try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* */ }
	}, []);

	const reset = useCallback(() => { start.current = null; setOffset({ x: 0, y: 0 }); }, []);

	const style: CSSProperties = offset.x || offset.y ? { translate: `${offset.x}px ${offset.y}px` } : {};
	return {
		offset, reset, style,
		// 손잡이(끄는 영역)에 펼친다 — touch-action: none 이 없으면 모바일에서 스크롤 제스처가 먼저 먹는다
		handleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, style: { touchAction: 'none' } as CSSProperties },
	};
}
